import React, {useEffect, useState} from "react";
import {Form, message} from "antd";
import {useDispatch} from "react-redux";
import {clearNewlyAddedCompany, saveCompany} from "../../redux/actions/companyActions";
import {useCompanySelector} from "../../redux/reducers/companyReducer";
import Axios from 'axios';
import api from "../../api/api";
import {companyFieldsBySteps, STEPS} from "./companyValidation";
import {useThemeSelector} from "../../redux/reducers/themeReducer";
import breakpoints from "../../constants/breakpoints";
import useWindowSize from "../../helpers/useWindowSize";
import {
    companyById_URL,
    companyHasSoftwareProduct_byCompanyId_URL,
    softwareProduct_URL
} from "../../constants/urlConfig";
import HttpResponseStatus from "../../constants/HttpResponseStatus";

const useController = ({pathname, history}) => {
    const dispatch = useDispatch();
    const {companySavePending, newCompany} = useCompanySelector();
    const {darkMode, primaryColor} = useThemeSelector();
    const size = useWindowSize();
    const [initialValues, setInitialValues] = useState({});
    const [initialValuesLoadingInEditMode, setInitialValuesLoadingInEditMode] = useState(false);
    const [softwareProducts, setSoftwareProducts] = useState([]);

    const [stepForm] = Form.useForm();
    const [current, setCurrent] = useState(0);
    const [stepsThatHaveError, setStepsThatHaveError] = useState([]);
    const [id, setId] = useState(null);

    const editMode = pathname.indexOf('editCompany') !== -1;
    const isDeviceSmall = size.width < breakpoints.sm;

    useEffect(async () => {
        const {data} = await api.get(softwareProduct_URL);
        setSoftwareProducts(data.map(({id, code}) => ({id, value: code})));

        if (editMode) {
            setInitialValuesLoadingInEditMode(true);
            const pathnameSplit = pathname.split('/');
            const id = pathnameSplit[pathnameSplit.length - 1];

            try {
                const [companyResponse, companyProductsResponse] = await Axios.all([
                    api.get(companyById_URL.replace('{id}', id)),
                    api.get(companyHasSoftwareProduct_byCompanyId_URL.replace('{id}', id))
                ]);
                const {data} = companyResponse;

                const areaCode = data.telephone.slice(0, 4);
                const number = data.telephone.slice(4, data.telephone.length);

                setInitialValuesLoadingInEditMode(false);
                setInitialValues({...data, telephone: {areaCode, number }, products: companyProductsResponse.data.map(({id, code}) => ({id, code}))});
                setId(id);
            } catch (error) {
                setInitialValuesLoadingInEditMode(false);
                if (error.response.status === HttpResponseStatus.NOT_FOUND) {
                    message.error({
                        content: 'Company not found.',
                        className: darkMode && "dark-mode"
                    });
                } else {
                    message.error({
                        content: 'Failed to load Company data. Please try again later.',
                        className: darkMode && "dark-mode"
                    });
                }
            }
        }
    }, []);

    useEffect(() => stepForm.resetFields(), [initialValues]);

    const next = () => {
        setCurrent(current + 1);
    };

    const prev = () => {
        setCurrent(current - 1);
    };

    const onChange = (current) => {
        setCurrent(current)
    };

    const goToAddLicencesForCompany = () => {
        history.push('/addLicence');
    };

    const goToCompanies = () => {
        history.push('/company');
    };

    const onProductsChange = (id, softwareProduct) => {
        const formProducts = stepForm.getFieldsValue().products;
        const products = formProducts.map(item => item && item.softwareProductId === id ? {
            ...item,
            code: softwareProduct.children + '_'
        } : item);

        stepForm.setFieldsValue({
            products
        });
    };

    const onSubmit = async () => {
        try {
            const values = await stepForm.validateFields();

            values.telephone = values.telephone.areaCode + values.telephone.number;

            dispatch(saveCompany(values, id));
        } catch (exc) {
            const stepsThatHaveError = [];

            STEPS.forEach(stepIndex => {
                const arr = companyFieldsBySteps[stepIndex];
                const thereIsAnError = arr.some(item => [].concat(...exc.errorFields.map(f => f.name)).includes(item));
                if (thereIsAnError)
                    stepsThatHaveError.push(stepIndex)
            });

            setStepsThatHaveError(stepsThatHaveError);

            message.error({
                content: `Data you provided is invalid. Please check ${stepsThatHaveError.length === 1 ?
                    'Step ' + (stepsThatHaveError[0] + 1) :
                    'Steps ' + stepsThatHaveError.map(i => i + 1).join(", ")}`,
                className: darkMode && "dark-mode"
            });
        }
    };

    return {
        current,
        stepsThatHaveError,
        next,
        prev,
        onChange,
        stepForm,
        onSubmit,
        initialValues,
        initialValuesLoadingInEditMode,
        companySavePending,
        goToAddLicencesForCompany,
        goToCompanies,
        newCompany,
        softwareProducts,
        onProductsChange,
        editMode,
        clearNewlyAddedCompany: () => {
            dispatch(clearNewlyAddedCompany())
        },
        darkMode,
        primaryColor,
        isDeviceSmall
    }
};

export default useController;